/**
 * Professional logging utility for the application.
 * Provides different log levels and can be easily configured for production.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  enabledLevels: LogLevel[];
  enabledInProduction: boolean;
}

const defaultConfig: LogConfig = {
  enabledLevels: ['error', 'warn'], // Only errors and warnings by default
  enabledInProduction: false, // Disable all logging in production
};

class Logger {
  private config: LogConfig;
  private isProduction: boolean;

  constructor(config: LogConfig = defaultConfig) {
    this.config = config;
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isProduction && !this.config.enabledInProduction) {
      return false;
    }
    return this.config.enabledLevels.includes(level);
  }

  private formatMessage(context: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const baseMessage = `[${timestamp}] [${context}] ${message}`;
    
    if (data) {
      return `${baseMessage}\nData: ${JSON.stringify(data, null, 2)}`;
    }
    
    return baseMessage;
  }

  debug(context: string, message: string, data?: any): void {
    if (this.shouldLog('debug')) {
      console.debug(this.formatMessage(context, message, data));
    }
  }

  info(context: string, message: string, data?: any): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage(context, message, data));
    }
  }

  warn(context: string, message: string, data?: any): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage(context, message, data));
    }
  }

  error(context: string, message: string, error?: any): void {
    if (this.shouldLog('error')) {
      const errorMessage = error instanceof Error 
        ? `${message}: ${error.message}\nStack: ${error.stack}`
        : this.formatMessage(context, message, error);
      
      console.error(`[${new Date().toISOString()}] [${context}] ${errorMessage}`);
    }
  }
}

// Export a singleton instance
export const logger = new Logger();

// Export types for use in other files
export type { LogLevel, LogConfig };
export { Logger }; 